<!-- eslint-disable max-len -->
<script lang="ts" setup>
import type { Component } from 'vue'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'

const bus = useEventBus('swym-app')
const isMobileDevice = useFeatureManager('app.config.mobile_device')

interface IModal {
  component: string
  _component: ReturnType<typeof defineAsyncComponent>
  props: Record<string | number | symbol, unknown>
}

const modals = shallowReactive<IModal[]>([])
/** When InContext search has to be opened in content creation modal */
const showModal = ref<boolean>(true)
/**
 * @info Please follow this modal naming convention:
 *       File name and modal key should start with "modal-" word,
 *       then the modal key should follow by the exact file name pattern.
 *
 * @info When adding/updating a new modal, please add the corresponding modal
 *       typping props and paylods definition into the ~/interfaces/components/modals.ts.
 */
const componentMatrix: Record<string, () => Promise<Component>> = {
  'modal-ai-conversation-creation': () => import('~/features-swym/aura/components/modal-ai-conversation-creation.vue'),
  'modal-ai-feedback': () => import('~/features-swym/aura/components/modal-ai-feedback.vue'),
  'modal-ai-generate': () => import('~/features-shared/ckeditor/components/modal-ai-generate.vue'),
  'modal-ai-translate': () => import('~/features-shared/ckeditor/components/modal-ai-translate.vue'),
  'modal-content-recovery': () => import('~/features-shared/content-creation/components/modal-content-recovery.vue'),
  'modal-content-conflict': () => import('~/features-shared/content-creation/components/modal-content-conflict.vue'),
  'modal-conversation-creation': () => import('~/features-shared/conversation/components/modal-conversation-creation.vue'),
  'modal-conversation-settings': () => import('~/features-shared/conversation/components/modal-conversation-settings.vue'),
  'modal-conversation-topic-edition': () => import('~/features-shared/conversation/components/modal-conversation-topic-edition.vue'),
  'modal-community-creation': () => import('~/features-swym/community/components/modal-community-creation.vue'),
  'modal-community-request-status': () => import('~/features-swym/community/components/modal-community-request-status.vue'),
  'modal-community-sign-terms-of-use': () => import('~/features-swym/community/components/modal-community-sign-terms-of-use.vue'),
  'modal-content-share': () => import('~/features-shared/content/components/modal-content-share.vue'),
  'modal-content-delete': () => import('~/features-shared/content/components/modal-content-delete.vue'),
  'modal-media-upload': () => import('~/features-shared/media-upload/components/modal-media-upload.vue'),
  'modal-media-upload-overlay': () => import('~/features-shared/media-upload/components/modal-media-upload-overlay.vue'),
  'modal-content-type-selector': () => import('~/features-shared/content-creation/components/modal-content-type-selector.vue'),
  'modal-content-creation': () => import('~/features-shared/content-creation/components/modal-content-creation.vue'),
  'modal-confirm-creation-cancel': () => import('~/features-shared/content-creation/components/modal-confirm-creation-cancel.vue'),
  'modal-confirm-draft-created': () => import('~/features-shared/content-creation/components/modal-confirm-draft-created.vue'),
  'modal-media-creation': () => import('~/features-shared/media-creation/components/modal-media-creation.vue'),
  'modal-community-member-add': () => import('~/features-swym/community/components/modal-community-member-add.vue'),
  'modal-member-remove': () => import('~/features-shared/member/components/modal-member-remove.vue'),
  'modal-forward': () => import('~/features-swym/forward/components/modal-forward.vue'),
  'modal-export-wiki-as-pdf': () => import('~/features-shared/wiki/components/modal-export-wiki-as-pdf.vue'),
  'modal-export-content-as-pdf': () => import('~/features-shared/export/modal-export-content-as-pdf.vue'),
  'modal-wiki-recover': () => import('~/features-swym/recycle-bin/components/modal-wiki-recover.vue'),
  // 'modal-add-contact': () => import('~/components/features/profile/add-contact-modal.vue'),
  'modal-become-author': () => import('~/features-swym/community/components/modal-become-author.vue'),
  'modal-community-signup-request': () => import('~/features-swym/community/components/modal-community-signup-request.vue'),
  'modal-concurrent-edition-warning': () => import('~/features-shared/content-creation/components/modal-concurrent-edition.vue'),
  'modal-replace-file': () => import('~/features-shared/media-lightbox/components/modal-replace-file.vue'),
  'modal-tags-edition': () => import('~/features-shared/tags/components/modal-tags-edition.vue'),
  'modal-container-unfollow': () => import('~/features-shared/unfollow/components/modal-container-unfollow.vue'),
  'modal-user-group-viewer': () => import('~/features-shared/member/components/modal-user-group-viewer.vue'),
  'modal-report-abuse': () => import('~/features-shared/report-abuse/components/modal-report-abuse.vue'),
  'modal-provider-selector': () => import('~/features-shared/conference/components/modal-provider-selector.vue'),
  'modal-ideation-status-deletion': () => import('~/features-shared/idea-edit/components/modal-ideation-status-deletion.vue'),
  'modal-export-members': () => import('~/features-shared/member/components/modal-export-members.vue'),
  'modal-import-members': () => import('~/features-shared/member/components/modal-import-members.vue'),
  'modal-remove-members': () => import('~/features-shared/member/components/modal-remove-members.vue'),
  'modal-display-batch-results': () => import('~/features-shared/member/components/modal-display-batch-results.vue'),
  'modal-confirm': () => import('~/features-shared/libs/components/modal-confirm.vue'),
  'm-media-extensions-size-table': () => import('~/features-shared/media-upload/components/modal-media-extensions-size-table.vue'),
  'network-user-add': () => import('~/features-swym/network/components/modal-network-user-add.vue'),
  'modal-select-community': () => import('~/features-shared/content-creation/components/modal-community-selector.vue'),
  'edit-wiki-permissions': () => import('~/features-shared/wiki-edit/components/modal-edit-wiki-permissions.vue'),
  'show-private-wiki-authorized-users': () => import('~/features-shared/wiki/components/modal-show-private-wiki-authorized-users.vue'),
  'transfer-idea-to-requirement': () => import('~/features-shared/idea-edit/components/modal-transfer-idea-to-requirement.vue'),
  'idea-related-link-creation': () => import('~/features-shared/idea-creation/components/modal-idea-related-link-creation.vue'),
  'change-idea-status': () => import('~/features-shared/idea-edit/components/modal-change-idea-status.vue'),
  'modal-media-extensions-size-table': () => import('~/features-shared/media-upload/components/modal-media-extensions-size-table.vue'),
  'modal-network-user-add': () => import('~/features-swym/network/components/modal-network-user-add.vue'),
  'modal-community-selector': () => import('~/features-shared/content-creation/components/modal-community-selector.vue'),
  'modal-community-information': () => import('~/features-swym/community/components/modal-community-information.vue'),
  'modal-edit-wiki-permissions': () => import('~/features-shared/wiki-edit/components/modal-edit-wiki-permissions.vue'),
  'modal-delete-wiki-page': () => import('~/entities/content/wiki/components/modal-delete-wiki-page.vue'),
  'modal-show-private-wiki-authorized-users': () => import('~/features-shared/wiki/components/modal-show-private-wiki-authorized-users.vue'),
  'modal-transfer-idea-to-requirement': () => import('~/features-shared/idea-edit/components/modal-transfer-idea-to-requirement.vue'),
  'modal-idea-related-link-creation': () => import('~/features-shared/idea-creation/components/modal-idea-related-link-creation.vue'),
  'modal-change-idea-status': () => import('~/features-shared/idea-edit/components/modal-change-idea-status.vue'),
  'modal-users-list-actions': () => import('~/features-swym/comment/components/modal-users-list-actions.vue'),
  'modal-profile-picture-edition': () => import('~/features-swym/profile/components/modal-profile-picture-edition.vue'),
  'profile-interest-add': () => import('~/features-swym/profile/components/modal-profile-interest-add.vue'),
  'profile-interest-list': () => import('~/features-swym/profile/components/modal-profile-interest-list-mobile.vue'),
  'profile-about-me': () => import('~/features-swym/profile/components/modal-profile-about-me.vue'),
  'profile-contact-info': () => import('~/features-swym/profile/components/modal-profile-contact-info.vue'),
  'profile-website-add': () => import('~/features-swym/profile/components/modal-profile-website-add.vue'),
  'profile-website-list': () => import('~/features-swym/profile/components/modal-profile-website-list-mobile.vue'),
  'profile-work-add': () => import('~/features-swym/profile/components/modal-profile-experience-add.vue'),
  'profile-work-list': () => import('~/features-swym/profile/components/modal-profile-experience-list-mobile.vue'),
  'profile-education-add': () => import('~/features-swym/profile/components/modal-profile-education-add.vue'),
  'profile-education-list': () => import('~/features-swym/profile/components/modal-profile-education-list-mobile.vue'),
  'profile-skill-add': () => import('~/features-swym/profile/components/modal-profile-skill-add.vue'),
  'profile-skill-list': () => import('~/features-swym/profile/components/modal-profile-skill-list-mobile.vue'),
  'profile-language-add': () => import('~/features-swym/profile/components/modal-profile-language-add.vue'),
  'profile-language-list': () => import('~/features-swym/profile/components/modal-profile-language-list-mobile.vue'),
  'modal-meeting-live': () => import('~/features-shared/conference/components/modal-meeting-live.vue'),
  'modal-moderate-button': () => import('~/features-shared/report-abuse/components/modal-moderation-button.vue'),
  'modal-close-manage-abuse': () => import('~/features-shared/report-abuse/components/modal-report-abuse-close.vue'),
  'modal-manage-abuse-closure-reason': () => import('~/features-shared/report-abuse/components/modal-report-abuse-closure-reason.vue'),
  'modal-video-message-recorder': () => import('~/features-swym/media-voice-message/components/modal-video-message-recorder.vue'),
  'modal-experience-configure': () => import('~/features-swym/community-settings/components/modal-experience-configure.vue'),
  'modal-experience-configure-icon-color': () => import('~/features-swym/community-settings/components/modal-experience-configure-icon-color.vue'),
  'modal-preview-configure': () => import('~/features-swym/community-settings/components/modal-preview-configure.vue'),
  'modal-section-creation': () => import('~/features-shared/sections/components/modal-section-creation.vue'),
  'modal-section-delete': () => import('~/features-shared/sections/components/modal-section-delete.vue'),
}

const getComponent = (component: string) => defineAsyncComponent(componentMatrix[component])

tryOnBeforeUnmount(bus.on((event, payload) => {
  if (event === 'modal-manager:open') {
    modals.push({
      component: payload.component,
      _component: getComponent(payload.component),
      props: payload.props,
    })
  }
  /**
   * Specific case when modal should be hidden to display InContext search panel
   */
  if (event === 'toggle-content-modal')
    showModal.value = payload
}))

function close(modal: IModal, args: unknown) {
  modals.splice(modals.indexOf(modal), 1)
  bus.emit(`modal-manager:${modal.component}:closed`, args)
}
</script>

<template>
  <template
    v-for="(modal, index) in modals"
    :key="`modal-manager-key-${modal.component}-${index}`"
  >
    <!-- z-index should be below 1000 as dropdown menu have z-index of 1000 -->
    <component
      :is="modal._component"
      v-bind="modal.props"
      v-show="showModal"
      class="modal-manager_component absolute left-0 top-0"
      :class="[{ 'is-mobile-device': isMobileDevice }]"
      show
      :style="`z-index: ${900 + index}`"
      @close="close(modal, $event)"
    />
  </template>
</template>

<style scoped>
.modal-manager_component + .modal-manager_component.is-mobile-device :deep(.modal) {
  @apply rounded-t-md mt-[44px];
}

:deep(.confirm-modal) {
  @apply h-fit block top-auto sm:top-20 bottom-0 animate-slide-bottom-to-top sm:animate-none;
  @apply bottom-0 !important;

  @media screen and (max-height: 600px) {
    @apply top-auto bottom-0 animate-slide-bottom-to-top;

    .modal-wrap {
      @apply max-w-none pb-0;

      .modal-content {
        @apply rounded-b-none;
      }

      .modal-body {
        @apply max-w-none gap-4;
      }

      .confirm-modal_image {
        @apply max-h-[20vh];
      }
    }
  }
}
</style>
